import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/card.module.scss'
import typography from 'styles/components/typography.module.scss'
import text from 'styles/utility/text.module.scss'
import { NotificationDescription, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { SmallLabel } from 'components/Label'
import { BlueTag, DisabledTag, GreyTag, TimeSaleTag } from 'components/Tag'
import { BlueButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { openStockModal } from 'reducks/modal/operations'
import { StockType } from 'types/stock'
import { dateToString, getBreadShortName, parseTimeToDate, sumCheckoutItemsAmount } from 'lib/helper'
import { BreadType } from 'types/bread'
import { ScheduleType } from 'types/schedule'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { FirebaseDocumentDataType, FirebaseSnapshotType } from 'firebase/index'
import { CheckoutItemType } from 'types/checkoutItem'
import { getLastOrderTime, getOrderStartTime } from 'reducks/store/selectors'
import { BusinessTimeType } from 'types/store'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { useEarliestBusinessTime, useLatestOrderStartTime } from 'hooks/businessHour'
import { useIsAfterLastOrder, useIsOrderable, useNextBusinessDate } from 'hooks/store'
import { isSameDay } from 'date-fns'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { useCanBeNotified, useLastNotifiedMinutes, useLastNotifiedTime, useRemainingStocksAmount } from 'hooks/stock'
import { StoreTimesale } from 'types/storeTimesale'
import { getStoreTimesale } from 'reducks/storeTimesale/selectors'
import classNames from 'classnames'
import format from 'date-fns/format'
const daysOfWeek = new DaysOfWeek()

type Props = {
  bread: BreadType
  isEditable?: boolean
  checkoutItems: CheckoutItemType[]
  stocks: StockType[]
}

const StockCard: FC<Props> = (props) => {
  const { bread, isEditable, checkoutItems, stocks } = props

  const dispatch = useDispatch()

  const closedDates = useSelector((state: State) => getClosedDates(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  // タイムセール対象の商品情報を取得
  const storeTimesale: StoreTimesale = useSelector((state: State) => getStoreTimesale(state))

  const now = new Date()
  const nextBusinessDate = useNextBusinessDate(closedDates)
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(now, specialHolidays)
  // 臨時営業日の取得（通常営業日ならundefined）
  const temporaryOpen = useGetTemporaryOpen(dateToString(now), storeCalendars)
  // 営業時間を取得
  const latestOrderStartTime = useLatestOrderStartTime([dayOfWeek], orderStartTime, temporaryOpen)
  const earliestLastOrderTime = useEarliestBusinessTime([dayOfWeek], lastOrderTime, 'last_order_time', temporaryOpen)
  const orderStartDateTime = parseTimeToDate(latestOrderStartTime)
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  const isOrderable = useIsOrderable(now, orderStartDateTime, lastOrderDateTime)
  const isAfterLastOrder = useIsAfterLastOrder(now, lastOrderDateTime)
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, now))
  // 30分以内に通知を送っているかどうか
  const canBeNotified = useCanBeNotified(stocks)
  const lastNotifiedMinutes = useLastNotifiedMinutes(stocks)
  const lastNotifiedTime = useLastNotifiedTime(stocks)
  const remainingStockAmount = useRemainingStocksAmount(stocks, checkoutItems)
  const checkoutItemAmount = sumCheckoutItemsAmount(checkoutItems)
  // 販売予定の商品がタイムセール対象商品か検証
  const isTimeSaleItem =
    storeTimesale?.is_active &&
    storeTimesale?.breads.some((targetBread) => targetBread.bread_id === bread.bread_ref?.id)

  const [schedules, setSchedules] = useState<ScheduleType[]>([])

  const openEditModal = useCallback(() => {
    dispatch(openStockModal(bread, schedules, stocks, isAfterLastOrder || isClosed ? nextBusinessDate : now))
  }, [dispatch, stocks, schedules, bread, isAfterLastOrder, isClosed, nextBusinessDate, now])

  // 対象のパンの当日取り置きの設定を取得する
  useEffect(() => {
    if (!bread || !storeRef || stocks.length > 0) return

    storeRef
      .collection('store_schedules')
      .where('bread_ref', '==', bread.bread_ref)
      .where('type', '==', 'daily')
      .get()
      .then((snapshots: FirebaseDocumentDataType) => {
        const _schedules: ScheduleType[] = []
        snapshots.forEach((snapshot: FirebaseSnapshotType) => {
          const data = snapshot.data() as ScheduleType
          data.schedule_ref = snapshot.ref
          _schedules.push(data)
        })
        setSchedules([..._schedules])
      })
  }, [stocks, bread, storeRef])

  return (
    <div className={styles.row__inner} data-cy={`stock-card-${bread.name}`}>
      <FlexRow justifyContent={'start'} alignItems={'start'}>
        <div className={styles.row__image}>
          <img src={bread.image_urls[0] || ''} alt={bread.name} />
        </div>
        <Spacer size={12} />
        <div className={styles.row__body}>
          <FlexRow justifyContent={'between'} alignItems={'start'}>
            <div>
              <h4 className={typography.headline_small}>
                {getBreadShortName(bread)}
                {isTimeSaleItem && <TimeSaleTag tagName={'セール対象'} />}
              </h4>
              <Spacer size={8} />
              {stocks.length === 0 ? (
                <DisabledTag dataCy={bread.name} size={'large'} tagName={'在庫が未設定です'} />
              ) : remainingStockAmount === 0 ? (
                <DisabledTag size={'large'} tagName={'在庫がありません'} />
              ) : (
                <FlexRow justifyContent={'start'}>
                  <span className={text.font_small}>在庫数 ： </span>
                  <Spacer size={2} />
                  {isEditable ? (
                    <BlueTag
                      dataCy={'stock-card-stock-number'}
                      shape={'ellipse'}
                      tagName={remainingStockAmount.toLocaleString()}
                    />
                  ) : (
                    <GreyTag shape={'ellipse'} tagName={remainingStockAmount.toLocaleString()} />
                  )}
                </FlexRow>
              )}
              <Spacer size={4} />
              <SubDescription description={`すでにこの商品が注文された数：${checkoutItemAmount}`} size={'small'} />
              {stocks[0] && (
                <>
                  <Spacer size={4} />
                  <p className={classNames(text.font_xsmall, text.font_bold)}>
                    注文締切：{format(stocks[0].deadline.toDate(), 'HH:mm')}
                  </p>
                </>
              )}
            </div>
            {isEditable && (
              <>
                <Spacer size={12} />
                <BlueButton
                  dataCy={`stock-card-edit-${bread.name}`}
                  label={'編集'}
                  onClick={openEditModal}
                  size={'xxsmall'}
                  type={'outlined'}
                />
              </>
            )}
          </FlexRow>
          {!isAfterLastOrder && !isClosed && (
            <>
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <SmallLabel label={'前回の通知：'} />
                <Spacer size={2} />
                <span className={text.font_xxsmall}>{lastNotifiedTime}</span>
              </FlexRow>
            </>
          )}
          {isEditable && !canBeNotified && isOrderable && (
            <NotificationDescription align={'left'} lastNotifiedMinutes={lastNotifiedMinutes} />
          )}
        </div>
      </FlexRow>
    </div>
  )
}

export default StockCard
